import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {Avatar, Space, Tooltip} from 'antd';
import {ChevronDownIcon} from '../../icons';
import {
  StyledNavigationBottomNavigation,
  StyledNavigationCompanyDetails,
  StyledNavigationCompanyName,
  StyledNavigationDropdownContainer,
  StyledNavigationDropdown,
  StyledNavigationMenu,
  StyledNavigationMenuSpin,
  StyledNavigation
} from './StyledNavigation';
import DropdownNavigation from './DropdownNavigation';
import {useCompanyName, useOnClickOutside} from '../../hooks';
import './Navigation.css';
import {mainActions} from '../../state/actions/main.actions';
import {KYCModalModeTypes} from '../../constants';
import {ReactComponent as WithLessIconLogo} from '../../static/images/withless-icon-logo.svg';
import {ReactComponent as WithLessLogo} from '../../static/images/withless-logo.svg';
import {elementHelpers, textHelpers} from '../../utils/helpers';

const Navigation = ({
  items = [],
  loading = false,
  navigationProps = {},
  ...rest
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Two leaf selectors, not the whole slice. `state.company` holds 23 keys and is written by 19
  // action types — tags, employees, team employees, settings, invoice email — so subscribing to the
  // object re-rendered this component, and its whole subtree, every time any of them loaded.
  // Navigation is mounted on every authenticated screen, so that was the widest re-render in the app
  // (H1MVP-4779). These are the only two fields it reads.
  const companyName = useCompanyName();
  const companyLogo = useSelector((state) => state.company?.logo);
  const isOpenMenu = useSelector((state) => state.main.isOpenMenu);
  // Creating a company is for backoffice-support staff only. GET /api/users/current is stored
  // verbatim by GET_USER_PROFILE, so this needs no reducer change — it reads false for everyone
  // until the backend starts sending the flag (H1MVP-4648), which is the current behaviour.
  const isBackofficeStaff = useSelector((state) => state.user?.user?.is_backoffice_staff ?? false);
  const [openKeys, setOpenKeys] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [collapsed, setCollapsed] = useState(true);
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);

  const menuItems = useMemo(() => {
    const dividedKeys = [...new Set(items.map(i => i.dividedKey).filter(i => Boolean(i)))];
    const undividedItems = items.filter(i => !dividedKeys.includes(i.dividedKey));
    let menuItems = [...undividedItems];

    if (Boolean(dividedKeys)) {
      dividedKeys.forEach(key => {
        const itemsByKey = items.filter(i => i.dividedKey === key);
        if (itemsByKey.length) {
          menuItems = [
            ...menuItems,
            {type: 'divider'},
            ...itemsByKey
          ];
        }
      })
    }

    return menuItems.map(({dividedKey, children, ...restItem}) => ({
      ...restItem,
      ...(children ? { children: children.map(({dividedKey, ...childRest}) => childRest) } : {})
    }));
  }, [items]);

  const collapsedClassName = useMemo(() => collapsed ? 'collapsed' : '', [collapsed]);

  useEffect(() => {
    if (isOpenMenu === collapsed) setCollapsed(!isOpenMenu);
  }, [isOpenMenu]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    elementHelpers.updateMenuItems({location, items, selectedKeys, setSelectedKeys});
  }, [location, items]); // eslint-disable-line react-hooks/exhaustive-deps

  const onCompanyChange = () => {
    navigate('/');
  }

  const onDropdownNavigationClick = () => {
    setIsOpenDropdown(false);
  }

  const handleMenuClick = (e) => {
    const { key } = e;
    let foundPath = null;
    let selectedKeyPath = [];

    for (let parent of items) {
      if (parent.key === key) {
        foundPath = parent.path;
        selectedKeyPath = [parent.key];
        break;
      }

      if (Array.isArray(parent.children)) {
        const child = parent.children.find(c => c.key === key);
        if (child) {
          foundPath = child.path;
          selectedKeyPath = [parent.key, child.key];
          break;
        }
      }
    }

    if (selectedKeyPath.length === 1 && openKeys.length > 0) {
      setOpenKeys([]);
    }

    if (foundPath) navigate(foundPath);
    setSelectedKeys(selectedKeyPath);
  };


  const onClickOutside = (e) => {
    const parentSelector = '.ant-dropdown';
    if (!e.target.closest(parentSelector)) setIsOpenDropdown(false);
  }

  const onAddCompany = () => {
    onDropdownNavigationClick();
    dispatch(mainActions.openKycWindow({mode: KYCModalModeTypes.COMPANY}));
  }

  const getCompanyDetails = (avatarSize) => {
    const name = companyName || '';
    const truncatedName = textHelpers.cutString(name, 35);
    const showTooltip = truncatedName.length < name.length && isOpenMenu;

    return (
      <StyledNavigationCompanyDetails>
        <Avatar
          src={companyLogo}
          size={avatarSize}
        >
          {textHelpers.getInitials(name)}
        </Avatar>
        <StyledNavigationCompanyName truncated={showTooltip}>
          {showTooltip ? (
            <Tooltip title={name}>{truncatedName}</Tooltip>
          ) : truncatedName}
        </StyledNavigationCompanyName>
      </StyledNavigationCompanyDetails>
    )
  };

  const handleOnOpenChange = (keys) => {
    if (!collapsed) {
      // rc-menu appends the newly opened key at the end of the array, not the start, so
      // keys?.[0] only matched the just-clicked parent when no submenu was open yet
      // (top-level -> 2-level). Diffing against the previous openKeys finds the parent
      // that was actually just opened regardless of what else was already open.
      const newKey = keys.find(key => !openKeys.includes(key));
      const firstChild = menuItems.find(c => c.key === newKey)?.children?.[0];
      if (firstChild) handleMenuClick(firstChild);
    }
    setOpenKeys(keys);
  }

  return (
    <StyledNavigation {...rest}>
      <div>
        <StyledNavigationDropdownContainer
          className={collapsedClassName}
          ref={useOnClickOutside(onClickOutside, isOpenDropdown)}
        >
          <StyledNavigationDropdown
            className={collapsedClassName}
            classNames={{root: 'navigation-dropdown-overlay'}}
            disabled={collapsed}
            popupRender={() => (
              <DropdownNavigation
                enabledCompanyCreation={isBackofficeStaff}
                enabledCompanySearch={isBackofficeStaff}
                onCompanyChange={onCompanyChange}
                onMenuClick={onDropdownNavigationClick}
                handleAddCompany={onAddCompany}
                open={isOpenDropdown}
              />
            )}
            onClick={() => setIsOpenDropdown(!isOpenDropdown)}
            open={isOpenDropdown}
            trigger={['click']}
          >
            <Space className='d-flex justify-space-between'>
              {getCompanyDetails(24)}
              <ChevronDownIcon />
            </Space>
          </StyledNavigationDropdown>
        </StyledNavigationDropdownContainer>
        <StyledNavigationMenuSpin size='small' spinning={loading}>
          <StyledNavigationMenu
            {...navigationProps}
            inlineCollapsed={collapsed}
            items={menuItems}
            expandIcon={null}
            mode='inline'
            onClick={handleMenuClick}
            onOpenChange={handleOnOpenChange}
            selectedKeys={selectedKeys}
            openKeys={openKeys}
          />
        </StyledNavigationMenuSpin>
      </div>
      <StyledNavigationBottomNavigation>
        <Link to='/' className='d-inline-flex'>
          {collapsed ? <WithLessIconLogo /> : <WithLessLogo />}
        </Link>
      </StyledNavigationBottomNavigation>
    </StyledNavigation>
  );
}

Navigation.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      disable: PropTypes.bool,
      key: PropTypes.any.isRequired,
      icon: PropTypes.object,
      isUnread: PropTypes.bool,
      label: PropTypes.string.isRequired,
      path: PropTypes.string || null
    })
  ).isRequired,
  loading: PropTypes.bool,
  navigationProps: PropTypes.object,
}

export default Navigation;
